import { makeStyles, tokens } from "@fluentui/react-components";
import { ArrowRight16Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { dolphinTokens } from "../../utils/Theme";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexWrap: "wrap",
        gap: "32px",
        maxWidth: "1312px",
        margin: "2.5rem auto 0",
        padding: "0 1rem",
        boxSizing: "border-box",
    },
    column: {
        position: "relative",
        flex: "1 1 240px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        padding: "28px 8px 8px 0",
        borderTop: `2px solid ${tokens.colorNeutralStroke2}`,
        transition: "border-color 0.18s ease",
        "&:hover": {
            borderTopColor: dolphinTokens.colorLightBlue,
        },
        "&:has(a:focus-visible)": {
            outlineWidth: "3px",
            outlineStyle: "solid",
            outlineColor: dolphinTokens.colorLightBlue,
            outlineOffset: "6px",
        },
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
        },
    },
    number: {
        fontSize: "3.5rem",
        lineHeight: 1,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: "-0.02em",
        color: dolphinTokens.colorLightBlue,
    },
    title: {
        margin: "8px 0 0",
        fontSize: "1.375rem",
        lineHeight: "1.25",
        fontWeight: tokens.fontWeightBold,
        color: tokens.colorNeutralForeground1,
    },
    description: {
        margin: 0,
        fontSize: tokens.fontSizeBase400,
        lineHeight: "1.6",
        color: tokens.colorNeutralForeground2,
    },
    cta: {
        marginTop: "auto",
        paddingTop: "16px",
        display: "inline-flex",
        alignItems: "center",
        alignSelf: "flex-start",
        gap: "8px",
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: dolphinTokens.colorLightBlue,
        textDecorationLine: "none",
        textUnderlineOffset: "4px",
        "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        },
        "&:hover": { textDecorationLine: "underline" },
        "&:focus-visible": { outlineStyle: "none" },
        "& svg": { transition: "transform 0.18s ease" },
        "&:hover svg": { transform: "translateX(4px)" },
        "@media (prefers-reduced-motion: reduce)": {
            "& svg": { transition: "none" },
            "&:hover svg": { transform: "none" },
        },
    },
});

export interface IKeyServicesCard {
    titleKey: string;
    descriptionKey: string;
    linkUrl: string;
    linkTextKey: string;
}

interface IKeyServicesProps {
    cards: IKeyServicesCard[];
}

export default function KeyServices({ cards }: IKeyServicesProps) {
    const { t } = useTranslation("home");
    const styles = useStyles();

    return (
        <div className={styles.root}>
            {cards.map((card, index) => (
                <article key={card.titleKey} className={styles.column}>
                    <span className={styles.number} aria-hidden="true">
                        {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className={styles.title}>{t(card.titleKey)}</h3>
                    <p className={styles.description}>{t(card.descriptionKey)}</p>
                    <a className={styles.cta} href={card.linkUrl}>
                        {t(card.linkTextKey)}
                        <ArrowRight16Regular aria-hidden="true" />
                    </a>
                </article>
            ))}
        </div>
    );
}